import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowRight, Sparkle, Plus, X, TrendUp, Users, 
  Buildings, MapPin, Quotes, CheckCircle, PaperPlaneTilt
} from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuccessStoriesPage = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formData, setFormData] = useState({
    organization: "",
    location: "",
    contactName: "",
    contactRole: "",
    contactEmail: "",
    title: "",
    challenge: "",
    solution: "",
    results: "",
    testimonial: ""
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/success-stories`);
      if (response.ok) {
        const data = await response.json();
        setStories(data.stories || []);
      }
    } catch (error) {
      console.error("Error fetching stories:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/success-stories`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          organization: "",
          location: "",
          contactName: "",
          contactRole: "",
          contactEmail: "",
          title: "",
          challenge: "",
          solution: "",
          results: "",
          testimonial: ""
        });
        setTimeout(() => {
          setShowAddForm(false);
          setSubmitSuccess(false);
        }, 3000);
      }
    } catch (error) {
      console.error("Error submitting story:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="success-stories-page">
      {/* Hero Section */}
      <section className="section-padding bg-hero-gradient relative overflow-hidden" data-testid="stories-hero">
        <div className="decorative-blob w-96 h-96 bg-primary/20 -top-48 -right-48" />
        <div className="decorative-blob w-72 h-72 bg-accent/20 bottom-0 left-0" />
        
        <div className="max-w-7xl mx-auto container-padding relative">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="badge badge-primary mb-6">
              <Sparkle weight="fill" className="w-4 h-4 mr-2" />
              Success Stories
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-foreground mb-6 leading-tight">
              Real Results,{" "}
              <span className="text-gradient">Real Transformations</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              Discover how Noble Hospice Solutions has helped hospice organizations 
              across the country achieve remarkable transformations in compliance, 
              growth, and operational excellence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stories Section */}
      <section className="section-padding" data-testid="stories-list">
        <div className="max-w-7xl mx-auto container-padding">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-muted-foreground">Loading success stories...</p>
            </div>
          ) : stories.length > 0 ? (
            <div className="space-y-16">
              {stories.map((story, index) => (
                <motion.div
                  key={story.id || index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-3xl shadow-lg border border-border overflow-hidden"
                  data-testid={`success-story-${index}`}
                >
                  <div className="bg-gradient-to-r from-primary to-primary/80 p-6 text-white">
                    <div className="flex items-center gap-2 text-white/80 text-sm mb-2">
                      <Buildings weight="duotone" className="w-4 h-4" />
                      <span>{story.organization}</span>
                      <span className="mx-2">•</span>
                      <MapPin weight="duotone" className="w-4 h-4" />
                      <span>{story.location}</span>
                    </div>
                    <h3 className="font-display text-2xl">{story.title}</h3>
                  </div>
                  <div className="p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
                            <span className="w-2 h-2 rounded-full bg-red-500" />
                          </span>
                          The Challenge
                        </h4>
                        <p className="text-muted-foreground">{story.challenge}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                            <CheckCircle weight="fill" className="w-4 h-4 text-green-500" />
                          </span>
                          Our Solution
                        </h4>
                        <p className="text-muted-foreground">{story.solution}</p>
                      </div>
                    </div>
                    {story.results && (
                      <div className="mt-6 p-4 bg-primary/5 rounded-xl">
                        <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                          <TrendUp weight="bold" className="w-5 h-5 text-primary" />
                          Results
                        </h4>
                        <p className="text-muted-foreground">{story.results}</p>
                      </div>
                    )}
                    {story.testimonial && (
                      <div className="mt-6 border-l-4 border-primary pl-6">
                        <Quotes weight="fill" className="w-8 h-8 text-primary/20 mb-2" />
                        <p className="text-foreground italic">"{story.testimonial}"</p>
                        <p className="text-sm text-muted-foreground mt-2">
                          — {story.contactName}, {story.contactRole}
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            /* Empty State */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Users weight="duotone" className="w-12 h-12 text-primary" />
              </div>
              <h2 className="font-display text-2xl md:text-3xl text-foreground mb-4">
                Be the First to Share Your Story
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto mb-8">
                We're building a collection of success stories from hospice organizations 
                we've partnered with. If you've worked with Noble Hospice Solutions, 
                we'd love to feature your transformation journey.
              </p>
              <button
                onClick={() => setShowAddForm(true)}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="add-story-btn"
              >
                <Plus weight="bold" className="w-5 h-5" />
                Share Your Success Story
              </button>
            </motion.div>
          )}

          {/* Add Story Button (when stories exist) */}
          {stories.length > 0 && (
            <div className="text-center mt-16">
              <button
                onClick={() => setShowAddForm(true)}
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="add-story-btn"
              >
                <Plus weight="bold" className="w-5 h-5" />
                Share Your Success Story
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Add Story Modal */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={(e) => e.target === e.currentTarget && setShowAddForm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto my-8"
              data-testid="add-story-modal"
            >
              {submitSuccess ? (
                <div className="p-12 text-center">
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle weight="fill" className="w-10 h-10 text-green-500" />
                  </div>
                  <h3 className="font-display text-2xl text-foreground mb-2">Thank You!</h3>
                  <p className="text-muted-foreground">
                    Your success story has been submitted for review. We'll be in touch soon!
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between p-6 border-b border-border">
                    <h3 className="font-display text-xl text-foreground">Share Your Success Story</h3>
                    <button
                      onClick={() => setShowAddForm(false)}
                      className="p-2 hover:bg-muted rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Organization Name *
                        </label>
                        <input
                          type="text"
                          name="organization"
                          value={formData.organization}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                          placeholder="e.g., Comfort Care Hospice"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Location *
                        </label>
                        <input
                          type="text"
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                          placeholder="e.g., California"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Your Name *
                        </label>
                        <input
                          type="text"
                          name="contactName"
                          value={formData.contactName}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                          placeholder="e.g., Sarah Johnson"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Your Role *
                        </label>
                        <input
                          type="text"
                          name="contactRole"
                          value={formData.contactRole}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                          placeholder="e.g., Executive Director"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="contactEmail"
                        value={formData.contactEmail}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Story Title *
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                        placeholder="e.g., 30% Census Growth in 90 Days"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        The Challenge You Faced *
                      </label>
                      <textarea
                        name="challenge"
                        value={formData.challenge}
                        onChange={handleInputChange}
                        required
                        rows={3}
                        className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                        placeholder="Describe the challenges your organization was facing..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        How Noble Healthcare Helped *
                      </label>
                      <textarea
                        name="solution"
                        value={formData.solution}
                        onChange={handleInputChange}
                        required
                        rows={3}
                        className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                        placeholder="Describe how we helped solve the challenge..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Results Achieved
                      </label>
                      <textarea
                        name="results"
                        value={formData.results}
                        onChange={handleInputChange}
                        rows={2}
                        className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                        placeholder="e.g., 30% census increase, zero audit deficiencies..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Your Testimonial
                      </label>
                      <textarea
                        name="testimonial"
                        value={formData.testimonial}
                        onChange={handleInputChange}
                        rows={3}
                        className="w-full px-4 py-3 rounded-xl border border-border focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                        placeholder="Share your experience working with Noble Healthcare..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full btn-primary inline-flex items-center justify-center gap-2"
                      data-testid="submit-story-btn"
                    >
                      {submitting ? (
                        <>
                          <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <PaperPlaneTilt weight="bold" className="w-5 h-5" />
                          Submit Your Story
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <section className="cta-section section-padding" data-testid="stories-cta">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 border border-white rounded-full" />
          <div className="absolute bottom-10 right-10 w-48 h-48 border border-white rounded-full" />
        </div>
        
        <div className="relative max-w-4xl mx-auto container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Sparkle weight="fill" className="w-12 h-12 text-white/80 mx-auto mb-6" />
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-white mb-6">
              Ready to Write Your Success Story?
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
              Join hospice organizations that have transformed their operations 
              with Noble Hospice Solutions. Your free consultation awaits.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-primary font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:shadow-xl hover:scale-105 inline-flex items-center justify-center gap-2"
                data-testid="stories-cta-contact"
              >
                Start Your Journey
                <ArrowRight weight="bold" className="w-5 h-5" />
              </Link>
              <a
                href="tel:+17183046214"
                className="border-2 border-white text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:bg-white/10 inline-flex items-center justify-center gap-2"
                data-testid="stories-cta-phone"
              >
                Call +1 (718) 304-6214
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SuccessStoriesPage;
